import styled from "@emotion/styled";
import { kebabCase } from "es-toolkit";
import type { CSSProperties } from "react";

import { FONT_WEIGHT_NORMAL } from "../utilities";
import { FieldValidationErrorMessage } from "./FieldValidationErrorMessage";
import { useFormContext } from "./FormContext";
import type { FormFieldProps } from "./FormField";
import { FormField } from "./FormField";
import type { FormValues } from "./shared";

// Used in composable properties panel components.
export const GridTwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 15px;
  row-gap: 10px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: 16px;
  margin-bottom: 10px;
`;

export const FormRow = styled.div<{ $boldLabel?: boolean }>`
  ${({ $boldLabel }) => $boldLabel === false && `--form-label-weight: ${FONT_WEIGHT_NORMAL};`}
  width: 100%;
  min-width: 0; // Prevents overflow in flex containers
  min-height: 28px;
  touch-action: none;
  display: var(--form-row-display, flex);
  flex-direction: column;
  grid-template-columns: var(--form-row-label-columns, none);
  align-items: var(--form-row-label-align, stretch);
  row-gap: 5px;
  column-gap: 10px;
  margin-bottom: var(--form-row-spacing, 0);
`;

type Props<T extends FormValues> = FormFieldProps<T> & {
  style?: CSSProperties;
  className?: string;
  boldLabel?: boolean;
};

export const FormRowField = <T extends FormValues>({ children, style, className, boldLabel, ...props }: Props<T>) => {
  const { dataTest: formDataTestPrefix } = useFormContext();

  const dataTest =
    props.dataTest ?? (formDataTestPrefix ? `${formDataTestPrefix}-${kebabCase(props.name)}` : kebabCase(props.name));

  return (
    <FormField {...props} dataTest={dataTest}>
      <FormRow data-test={dataTest} style={style} className={className} $boldLabel={boldLabel}>
        {children}
        <FieldValidationErrorMessage />
      </FormRow>
    </FormField>
  );
};
